import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Input from '@mui/material/Input'
import Clear from '@mui/icons-material/Clear'
import ExpandMore from '@mui/icons-material/ExpandMore'

/**
 * File Details section for Bulk Edit Bar.
 * Editable fields: Filename, Creator, Source, Model name / Agency (chip input).
 */
const FileDetailsCard = ({
  updateParentState = () => { },
  selectedAssetGoldMetadata = {},
  usageInstructions: usageInstructionsProp = {},
}) => {
  const fileMetadata =
    selectedAssetGoldMetadata?.metadata?.file_metadata || {}

  const [fileBaseName, setFileBaseName] = useState('')
  const [fileExtension, setFileExtension] = useState('')
  const [creator, setCreator] = useState('')
  const [source, setSource] = useState('')
  const [personShown, setPersonShown] = useState([])
  const [usageInstructions, setUsageInstructions] = useState(usageInstructionsProp)

  // Sync usage instructions from prop
  useEffect(() => {
    setUsageInstructions(usageInstructionsProp)
  }, [usageInstructionsProp])

  // Split a filename into base name and extension
  const splitFileName = (name = '') => {
    const lastDot = name.lastIndexOf('.')
    if (lastDot > 0) {
      return { base: name.substring(0, lastDot), ext: name.substring(lastDot) }
    }
    return { base: name, ext: '' }
  }

  // Sync from gold metadata
  useEffect(() => {
    const { base, ext } = splitFileName(fileMetadata.file_name || '')
    setFileBaseName(base)
    setFileExtension(ext)
    setCreator(fileMetadata.creator || '')
    setSource(fileMetadata.source || '')
    setPersonShown(
      Array.isArray(fileMetadata.person_shown)
        ? fileMetadata.person_shown
        : [],
    )
  }, [selectedAssetGoldMetadata])

  // Propagate changes up
  const propagate = useCallback(
    (overrides = {}) => {
      const mergedBase = overrides.fileBaseName ?? fileBaseName
      const merged = {
        fileName: mergedBase + fileExtension,
        creator: overrides.creator ?? creator,
        source: overrides.source ?? source,
        personShown: overrides.personShown ?? personShown,
      }
      updateParentState({
        assetDetails: {
          fileName: merged.fileName,
          creator: merged.creator,
          source: merged.source,
          personShown: merged.personShown,
        },
        unsavedChanges: true,
      })
    },
    [updateParentState, fileBaseName, fileExtension, creator, source, personShown],
  )

  const handleAddPerson = useCallback(
    (e) => {
      if (e.key === 'Enter' && e.target.value.trim() !== '') {
        const newPerson = e.target.value.trim()
        setPersonShown((prev) => {
          const updated = [...prev, newPerson]
          propagate({ personShown: updated })
          return updated
        })
        e.target.value = ''
      }
    },
    [propagate],
  )

  const handleDeletePerson = useCallback(
    (personToDelete) => {
      setPersonShown((prev) => {
        const updated = prev.filter((p) => p !== personToDelete)
        propagate({ personShown: updated })
        return updated
      })
    },
    [propagate],
  )

  const handleUsageChange = useCallback(
    (fieldName) => (event) => {
      const value =
        fieldName === 'doNotUse'
          ? !usageInstructions.doNotUse
          : event.target.value
      const updated = {
        ...usageInstructions,
        unsavedChanges: true,
        disableSave: true,
        [fieldName]: value,
      }
      setUsageInstructions(updated)
      updateParentState({ usageInstructions: updated })
    },
    [usageInstructions, updateParentState],
  )

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="file-details-content"
        id="file-details-header"
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
          File Details
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Filename */}
          <Grid item xs={12}>
            <TextField
              id="fileName"
              label="Filename"
              value={fileBaseName}
              onChange={(e) => {
                setFileBaseName(e.target.value)
                propagate({ fileBaseName: e.target.value })
              }}
              fullWidth
              variant="outlined"
              inputProps={{ 'data-cy': 'fileNameBulk' }}
              InputProps={fileExtension ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      {fileExtension}
                    </Typography>
                  </InputAdornment>
                ),
              } : undefined}
            />
          </Grid>

          {/* Creator */}
          <Grid item xs={12} sm={6}>
            <TextField
              id="creatorBulk"
              label="Creator"
              value={creator}
              onChange={(e) => {
                setCreator(e.target.value)
                propagate({ creator: e.target.value })
              }}
              fullWidth
              variant="outlined"
              multiline
              inputProps={{ 'data-cy': 'creatorBulk' }}
            />
          </Grid>

          {/* Source */}
          <Grid item xs={12} sm={6}>
            <TextField
              id="sourceBulk"
              label="Source"
              value={source}
              onChange={(e) => {
                setSource(e.target.value)
                propagate({ source: e.target.value })
              }}
              fullWidth
              variant="outlined"
              multiline
              placeholder="Photographer or Studio Name"
              inputProps={{ 'data-cy': 'sourceBulk' }}
            />
          </Grid>

          {/* Model name / Agency (chip input) */}
          <Grid item xs={12}>
            <TextField
              id="personShown"
              label="Model Name / Agency"
              InputProps={{
                'data-cy': 'PersonShownMetaData',
                startAdornment: personShown.length > 0 && (
                  <InputAdornment position="start">
                    {personShown.map((person, index) => (
                      <Chip
                        key={index}
                        label={person}
                        onDelete={() => handleDeletePerson(person)}
                        sx={{
                          margin: '0 0.2rem',
                          backgroundColor: '#C8E6C9',
                          color: '#2E7D32',
                        }}
                        deleteIcon={
                          <Clear
                            style={{
                              width: '16px',
                              height: '16px',
                              color: '#2E7D32',
                            }}
                          />
                        }
                      />
                    ))}
                  </InputAdornment>
                ),
              }}
              onKeyDown={handleAddPerson}
              placeholder="Add Model Name / Agency"
              helperText="Use format Name/Agency. Press Enter after each entry."
              sx={{
                '& .MuiFormHelperText-root': {
                  color: '#d32f2f',
                },
              }}
              fullWidth
              variant="outlined"
            />
          </Grid>
          {/* Creative Usage Instructions */}
          <Grid item xs={12} sm={6}>
            <InputLabel htmlFor="instructions">Creative Usage Instructions</InputLabel>
            <FormControlLabel
              control={
                <Checkbox
                  checked={usageInstructions.doNotUse || false}
                  onChange={handleUsageChange('doNotUse')}
                />
              }
              label="Do Not Use"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl variant="standard" fullWidth>
              <InputLabel htmlFor="instructions">Instructions</InputLabel>
              <Input
                id="instructions"
                value={usageInstructions.instructions || ''}
                onChange={handleUsageChange('instructions')}
                multiline
              />
            </FormControl>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

FileDetailsCard.propTypes = {
  updateParentState: PropTypes.func,
  selectedAssetGoldMetadata: PropTypes.object,
  usageInstructions: PropTypes.shape({
    doNotUse: PropTypes.bool,
    instructions: PropTypes.string,
  }),
}

export default FileDetailsCard
